import { Component } from '@theme/component';

const CLOSE_DELAY = 180;
const SAFETY_TRIANGLE_MARGIN = 18;

/**
 * Animated desktop header menu with a lightweight state model:
 * - page active trigger
 * - currently expanded trigger/panel
 *
 * @typedef {object} Refs
 * @property {HTMLElement} surface
 * @property {HTMLElement} list
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['surface', 'list'];

  #abortController = new AbortController();

  #closeTimeout = 0;

  #triggers = [];

  #panels = [];

  #pageActiveTrigger = null;

  #expandedTrigger = null;

  #pointerPosition = null;

  #lastTriggerRect = null;

  #suppressFocusTrigger = null;

  #lastPointerType = '';

  #lastPointerTime = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#cacheNodes();
    this.#pageActiveTrigger = this.#findPageActiveTrigger();

    this.#bindEvents();
    this.#closeExpandedPanel();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    window.clearTimeout(this.#closeTimeout);
  }

  #cacheNodes() {
    this.#triggers = [...this.querySelectorAll('[data-menu-trigger]')];
    this.#panels = [...this.querySelectorAll('[data-menu-panel]')];
  }

  #bindEvents() {
    for (const trigger of this.#triggers) {
      trigger.addEventListener('pointerenter', this.#handleTriggerPointerEnter, {
        signal: this.#abortController.signal,
      });
      trigger.addEventListener('pointerdown', this.#handleTriggerPointerDown, {
        signal: this.#abortController.signal,
      });
      trigger.addEventListener('click', this.#handleTriggerClick, {
        signal: this.#abortController.signal,
      });
      trigger.addEventListener('focusin', this.#handleTriggerFocus, {
        signal: this.#abortController.signal,
      });
    }

    this.addEventListener('pointerleave', this.#scheduleClose, {
      signal: this.#abortController.signal,
    });
    this.addEventListener('pointermove', this.#handlePointerMove, {
      signal: this.#abortController.signal,
    });
    this.addEventListener('focusout', this.#handleFocusOut, {
      signal: this.#abortController.signal,
    });
    this.addEventListener('keydown', this.#handleKeydown, {
      signal: this.#abortController.signal,
    });
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown, {
      capture: true,
      signal: this.#abortController.signal,
    });
  }

  #handleTriggerPointerEnter = (event) => {
    if (this.#isTouchLikePointerEvent(event)) return;

    const trigger = this.#findTrigger(event.currentTarget);
    if (!trigger) return;

    this.#activateTrigger(trigger);
  };

  #handleTriggerPointerDown = (event) => {
    this.#lastPointerType = event.pointerType || '';
    this.#lastPointerTime = Date.now();
  };

  #handleTriggerClick = (event) => {
    const trigger = this.#findTrigger(event.currentTarget);
    if (!trigger || !this.#isTouchLikeActivation(event)) return;

    const panel = this.#findPanelByTrigger(trigger);
    if (!panel || this.#expandedTrigger === trigger) return;

    event.preventDefault();
    this.#activateTrigger(trigger);
  };

  #handleTriggerFocus = (event) => {
    const trigger = this.#findTrigger(event.currentTarget);
    if (!trigger) return;

    if (this.#hadRecentTouchPointer()) return;

    if (this.#suppressFocusTrigger === trigger) {
      this.#suppressFocusTrigger = null;
      return;
    }

    this.#activateTrigger(trigger);
  };

  #handlePointerMove = (event) => {
    this.#pointerPosition = { x: event.clientX, y: event.clientY };
  };

  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;

    this.#scheduleClose();
  };

  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    event.stopPropagation();
    this.#closeAndReturnFocus();
  };

  #handleDocumentPointerDown = (event) => {
    if (!this.#expandedTrigger) return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.#cancelClose();
    this.#resetToPageActive();
  };

  #activateTrigger(trigger) {
    this.#cancelClose();
    this.#lastTriggerRect = trigger.getBoundingClientRect();

    const panel = this.#findPanelByTrigger(trigger);
    if (!panel) {
      this.#closeExpandedPanel();
      return;
    }

    this.#openPanel(trigger, panel);
  }

  #openPanel(trigger, panel) {
    this.#expandedTrigger = trigger;

    for (const item of this.#triggers) {
      const isExpanded = item === trigger;
      if (item.hasAttribute('aria-haspopup')) {
        item.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      }
      item.parentElement?.classList.toggle('is-panel-active', isExpanded);
    }

    for (const item of this.#panels) {
      const isOpen = item === panel;
      item.classList.toggle('is-open', isOpen);
      item.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      item.toggleAttribute('inert', !isOpen);
    }

    this.dataset.panelOpen = 'true';
  }

  #closeExpandedPanel() {
    this.#expandedTrigger = null;

    for (const trigger of this.#triggers) {
      if (trigger.hasAttribute('aria-expanded')) {
        trigger.setAttribute('aria-expanded', 'false');
      }
      trigger.parentElement?.classList.remove('is-panel-active');
    }

    for (const panel of this.#panels) {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      panel.setAttribute('inert', '');
    }

    this.dataset.panelOpen = 'false';
  }

  #scheduleClose = () => {
    this.#cancelClose();
    this.#closeTimeout = window.setTimeout(() => {
      if (this.#isPointerInSafetyTriangle()) {
        this.#scheduleClose();
        return;
      }

      this.#resetToPageActive();
    }, CLOSE_DELAY);
  };

  #cancelClose() {
    window.clearTimeout(this.#closeTimeout);
  }

  #resetToPageActive() {
    this.#closeExpandedPanel();
  }

  #closeAndReturnFocus() {
    const returnFocusTarget = this.#expandedTrigger || this.#pageActiveTrigger || this.#triggers[0] || null;
    this.#suppressFocusTrigger = returnFocusTarget;
    this.#resetToPageActive();
    returnFocusTarget?.focus();
  }

  #findPageActiveTrigger() {
    return this.#triggers.find((trigger) => trigger.dataset.pageActive === 'true') || this.#triggers[0] || null;
  }

  #findTrigger(target) {
    return target instanceof HTMLElement ? target.closest('[data-menu-trigger]') : null;
  }

  #findPanelByTrigger(trigger) {
    const panelId = trigger?.dataset.menuId;
    return panelId ? this.querySelector(`[data-menu-panel="${panelId}"]`) : null;
  }

  #isTouchLikePointerEvent(event) {
    return event.pointerType === 'touch';
  }

  #isTouchLikeActivation(event) {
    const isCoarsePointerClick = event.detail > 0 && window.matchMedia('(hover: none), (pointer: coarse)').matches;

    return this.#hadRecentTouchPointer() || isCoarsePointerClick;
  }

  #hadRecentTouchPointer() {
    return this.#lastPointerType === 'touch' && Date.now() - this.#lastPointerTime < 1000;
  }

  #isPointerInSafetyTriangle() {
    const panel = this.#expandedTrigger ? this.#findPanelByTrigger(this.#expandedTrigger) : null;

    if (!(panel instanceof HTMLElement) || !this.#lastTriggerRect || !this.#pointerPosition) {
      return false;
    }

    const panelRect = panel.getBoundingClientRect();
    const point = this.#pointerPosition;

    if (point.x >= panelRect.left && point.x <= panelRect.right && point.y >= panelRect.top && point.y <= panelRect.bottom) {
      return true;
    }

    if (point.y < this.#lastTriggerRect.bottom || point.y > panelRect.bottom) {
      return false;
    }

    const apex = {
      x: clamp(point.x, this.#lastTriggerRect.left, this.#lastTriggerRect.right),
      y: this.#lastTriggerRect.bottom,
    };

    const leftTriangle = [
      apex,
      { x: panelRect.left - SAFETY_TRIANGLE_MARGIN, y: panelRect.top },
      { x: panelRect.left - SAFETY_TRIANGLE_MARGIN, y: panelRect.bottom },
    ];

    const rightTriangle = [
      apex,
      { x: panelRect.right + SAFETY_TRIANGLE_MARGIN, y: panelRect.top },
      { x: panelRect.right + SAFETY_TRIANGLE_MARGIN, y: panelRect.bottom },
    ];

    return pointInTriangle(point, leftTriangle) || pointInTriangle(point, rightTriangle);
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function pointInTriangle(point, triangle) {
  const [a, b, c] = triangle;
  const area = triangleArea(a, b, c);
  const area1 = triangleArea(point, b, c);
  const area2 = triangleArea(a, point, c);
  const area3 = triangleArea(a, b, point);

  return Math.abs(area - (area1 + area2 + area3)) < 0.5;
}

function triangleArea(a, b, c) {
  return Math.abs((a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y)) / 2);
}
